/**
 * CommentsPanel - Comments & Chat module for projects
 *
 * Two scopes:
 * - peer: Task-level comments (only assignees)
 * - team: Project-wide team chat
 *
 * External delivery channels:
 * - internal (default)
 * - whatsapp
 * - telegram
 * - email
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  MessageSquare,
  Send,
  Users,
  User,
  Link2,
  Mail,
  Phone,
  AtSign,
  Paperclip,
  Check,
  CheckCheck,
  ChevronDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from '../../hooks';
import type { Comment, CommentScope, CommentDeliveryChannel, Task, User as UserType } from '@business-assistant/shared/domain';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    background: '#fff',
    borderRadius: '8px',
    border: '1px solid #e5e5e5',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: '400px',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid #e5e5e5',
    background: '#fafafa',
  } as React.CSSProperties,
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '14px',
  } as React.CSSProperties,
  tabs: {
    display: 'flex',
    gap: '4px',
    padding: '8px 16px',
    borderBottom: '1px solid #e5e5e5',
  } as React.CSSProperties,
  tab: {
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  } as React.CSSProperties,
  tabActive: {
    background: '#e0e7ff',
    color: '#3b5998',
    fontWeight: 500,
  } as React.CSSProperties,
  messageList: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  } as React.CSSProperties,
  messageGroup: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: '#e0e7ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '12px',
    fontWeight: 600,
    color: '#3b5998',
  } as React.CSSProperties,
  messageContent: {
    flex: 1,
    minWidth: 0,
  } as React.CSSProperties,
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  } as React.CSSProperties,
  authorName: {
    fontWeight: 600,
    fontSize: '13px',
  } as React.CSSProperties,
  messageTime: {
    fontSize: '11px',
    color: '#888',
  } as React.CSSProperties,
  messageBubble: {
    padding: '8px 12px',
    background: '#f5f5f5',
    borderRadius: '8px',
    fontSize: '13px',
    lineHeight: 1.4,
  } as React.CSSProperties,
  messageBubbleOwn: {
    background: '#e0e7ff',
  } as React.CSSProperties,
  messageFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '4px',
    fontSize: '11px',
    color: '#888',
  } as React.CSSProperties,
  deliveryBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '10px',
    background: '#f0f0f0',
  } as React.CSSProperties,
  taskLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    background: '#fff3e0',
    borderRadius: '4px',
    fontSize: '11px',
    color: '#e65100',
    marginBottom: '6px',
    cursor: 'pointer',
  } as React.CSSProperties,
  composer: {
    padding: '12px 16px',
    borderTop: '1px solid #e5e5e5',
    background: '#fafafa',
  } as React.CSSProperties,
  composerOptions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  } as React.CSSProperties,
  deliverySelect: {
    display: 'flex',
    gap: '4px',
  } as React.CSSProperties,
  deliveryBtn: {
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    background: '#fff',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  } as React.CSSProperties,
  deliveryBtnActive: {
    background: '#e0e7ff',
    borderColor: '#3b5998',
    color: '#3b5998',
  } as React.CSSProperties,
  inputRow: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  textInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    resize: 'none',
    minHeight: '40px',
    maxHeight: '120px',
    fontFamily: 'inherit',
  } as React.CSSProperties,
  sendBtn: {
    padding: '0 16px',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    fontWeight: 500,
  } as React.CSSProperties,
  sendBtnDisabled: {
    background: '#ccc',
    cursor: 'not-allowed',
  } as React.CSSProperties,
  emptyState: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#888',
    padding: '40px',
  } as React.CSSProperties,
  mentionChip: {
    background: '#e0e7ff',
    color: '#3b5998',
    padding: '0 4px',
    borderRadius: '3px',
    fontWeight: 500,
  } as React.CSSProperties,
};

// =============================================================================
// TYPES
// =============================================================================

export interface CommentsPanelProps {
  comments: Comment[];
  tasks?: Task[];
  users?: UserType[];
  currentUserId: string;
  projectId: string;
  selectedTaskId?: string;
  onSendComment?: (text: string, scope: CommentScope, deliveryChannels: CommentDeliveryChannel[], taskId?: string) => void;
  onDeleteComment?: (id: string) => void;
  onEditComment?: (id: string, newText: string) => void;
  onTaskClick?: (taskId: string) => void;
}

// =============================================================================
// HELPERS
// =============================================================================

const DELIVERY_ICONS: Record<CommentDeliveryChannel, React.ReactNode> = {
  internal: <MessageSquare size={10} />,
  whatsapp: <Phone size={10} />,
  telegram: <Send size={10} />,
  email: <Mail size={10} />,
};

const DELIVERY_LABELS: Record<CommentDeliveryChannel, string> = {
  internal: 'App',
  whatsapp: 'WA',
  telegram: 'TG',
  email: 'Email',
};

// formatTime is now handled inside the component with locale awareness

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function renderTextWithMentions(text: string): React.ReactNode {
  // Simple mention detection: @name
  const parts = text.split(/(@\w+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} style={styles.mentionChip}>
          {part}
        </span>
      );
    }
    return part;
  });
}

// =============================================================================
// COMPONENT
// =============================================================================

export function CommentsPanel({
  comments,
  tasks = [],
  users = [],
  currentUserId,
  projectId,
  selectedTaskId,
  onSendComment,
  onDeleteComment,
  onEditComment,
  onTaskClick,
}: CommentsPanelProps) {
  const { t, language } = useTranslation();
  const locale = language === 'it' ? 'it-IT' : 'en-US';

  // Locale-aware time formatting
  const formatTime = (dateStr: string): string => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();

    if (diff < 60000) return t('common.now');
    if (diff < 3600000) return t('common.minutesAgo', { count: Math.floor(diff / 60000) });
    if (diff < 86400000) return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    return d.toLocaleDateString(locale, {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // State
  const [activeScope, setActiveScope] = useState<CommentScope>('team');
  const [messageText, setMessageText] = useState('');
  const [deliveryChannels, setDeliveryChannels] = useState<Set<CommentDeliveryChannel>>(new Set(['internal']));
  const listRef = useRef<HTMLDivElement>(null);

  // Build lookups
  const taskMap = useMemo(() => {
    const map = new Map<string, Task>();
    tasks.forEach((t) => map.set(t.id, t));
    return map;
  }, [tasks]);

  const userMap = useMemo(() => {
    const map = new Map<string, UserType>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  // Filter comments by scope
  const filteredComments = useMemo(() => {
    return comments
      .filter((c) => {
        if (c.scope !== activeScope) return false;
        if (activeScope === 'peer' && selectedTaskId && c.taskId !== selectedTaskId) return false;
        return true;
      })
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [comments, activeScope, selectedTaskId]);

  // Count unread (mock - would need proper unread tracking)
  const teamCount = comments.filter((c) => c.scope === 'team').length;
  const peerCount = comments.filter((c) => c.scope === 'peer').length;

  // Auto-scroll to bottom
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [filteredComments.length]);

  // Toggle delivery channel
  const toggleDelivery = (channel: CommentDeliveryChannel) => {
    setDeliveryChannels((prev) => {
      const next = new Set(prev);
      if (channel === 'internal') {
        // Internal always on
        return next;
      }
      if (next.has(channel)) {
        next.delete(channel);
      } else {
        next.add(channel);
      }
      return next;
    });
  };

  // Send message
  const handleSend = () => {
    if (!messageText.trim()) return;
    onSendComment?.(
      messageText.trim(),
      activeScope,
      Array.from(deliveryChannels),
      activeScope === 'peer' ? selectedTaskId : undefined
    );
    setMessageText('');
  };

  // Keyboard handling
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMessage = (comment: Comment) => {
    const isOwn = comment.authorId === currentUserId;
    const author = userMap.get(comment.authorId);
    const authorName = comment.authorName || author?.displayName || comment.authorId.slice(-6);
    const task = comment.taskId ? taskMap.get(comment.taskId) : null;

    return (
      <div key={comment.id} style={styles.messageGroup}>
        {!isOwn && (
          <div style={styles.avatar}>
            {author?.avatarUrl ? (
              <img src={author.avatarUrl} alt={authorName} style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
            ) : (
              getInitials(authorName)
            )}
          </div>
        )}

        <div style={{ ...styles.messageContent, marginLeft: isOwn ? 'auto' : 0, maxWidth: '70%' }}>
          {/* Task link for peer comments */}
          {task && activeScope === 'peer' && (
            <div style={styles.taskLink} onClick={() => onTaskClick?.(task.id)}>
              <Link2 size={12} />
              {task.title}
            </div>
          )}

          {/* Header */}
          <div style={styles.messageHeader}>
            <span style={styles.authorName}>{authorName}</span>
            <span style={styles.messageTime}>{formatTime(comment.createdAt)}</span>
          </div>

          {/* Bubble */}
          <div style={{ ...styles.messageBubble, ...(isOwn ? styles.messageBubbleOwn : {}) }}>
            {renderTextWithMentions(comment.text)}
          </div>

          {/* Footer */}
          <div style={styles.messageFooter}>
            {/* Delivery channels */}
            {comment.deliveredVia && comment.deliveredVia.length > 0 && (
              <div style={{ display: 'flex', gap: '4px' }}>
                {comment.deliveredVia.map((ch) => (
                  <span key={ch} style={styles.deliveryBadge}>
                    {DELIVERY_ICONS[ch]}
                    {DELIVERY_LABELS[ch]}
                  </span>
                ))}
              </div>
            )}

            {/* Read status (mock) */}
            {isOwn && (
              <span style={{ marginLeft: 'auto' }}>
                <CheckCheck size={12} color="#22c55e" />
              </span>
            )}
          </div>
        </div>

        {isOwn && (
          <div style={styles.avatar}>
            {getInitials(authorName)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <MessageSquare size={16} />
          {t('comments.title')}
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{ ...styles.tab, ...(activeScope === 'team' ? styles.tabActive : {}) }}
          onClick={() => setActiveScope('team')}
        >
          <Users size={14} />
          {t('comments.team')}
          {teamCount > 0 && <span style={{ fontSize: '11px', opacity: 0.7 }}>({teamCount})</span>}
        </button>
        <button
          style={{ ...styles.tab, ...(activeScope === 'peer' ? styles.tabActive : {}) }}
          onClick={() => setActiveScope('peer')}
        >
          <User size={14} />
          {t('comments.task')}
          {peerCount > 0 && <span style={{ fontSize: '11px', opacity: 0.7 }}>({peerCount})</span>}
        </button>
      </div>

      {/* Message list */}
      <div ref={listRef} style={styles.messageList}>
        {filteredComments.length === 0 ? (
          <div style={styles.emptyState}>
            <MessageSquare size={32} color="#ccc" style={{ marginBottom: '8px' }} />
            <p style={{ margin: 0 }}>
              {activeScope === 'team'
                ? t('comments.noTeamMessages')
                : selectedTaskId
                ? t('comments.noTaskComments')
                : t('comments.selectTaskToView')}
            </p>
          </div>
        ) : (
          filteredComments.map(renderMessage)
        )}
      </div>

      {/* Composer */}
      <div style={styles.composer}>
        {/* Delivery options */}
        <div style={styles.composerOptions}>
          <span style={{ fontSize: '11px', color: '#888' }}>{t('comments.sendAlsoVia')}</span>
          <div style={styles.deliverySelect}>
            {(['internal', 'whatsapp', 'telegram', 'email'] as CommentDeliveryChannel[]).map((ch) => (
              <button
                key={ch}
                style={{
                  ...styles.deliveryBtn,
                  ...(deliveryChannels.has(ch) ? styles.deliveryBtnActive : {}),
                  ...(ch === 'internal' ? { cursor: 'default' } : {}),
                }}
                onClick={() => toggleDelivery(ch)}
                disabled={ch === 'internal'}
              >
                {DELIVERY_ICONS[ch]}
                {DELIVERY_LABELS[ch]}
              </button>
            ))}
          </div>
        </div>

        {/* Input */}
        <div style={styles.inputRow}>
          <textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              activeScope === 'team' ? t('comments.writeToTeam') : t('comments.commentOnTask')
            }
            style={styles.textInput}
            rows={1}
          />
          <button
            onClick={handleSend}
            style={{
              ...styles.sendBtn,
              ...(messageText.trim() ? {} : styles.sendBtnDisabled),
            }}
            disabled={!messageText.trim()}
          >
            <Send size={16} />
            {t('common.send')}
          </button>
        </div>
      </div>
    </div>
  );
}
